import { Button, Card, Col, Form, Input, Row, message } from "antd";
import { Content } from "antd/lib/layout/layout";
import React, { useRef, useState } from "react";
import ApiClient from "../../helpers/ApiClient";

const SYT_HOTEL_REF = /^SYT-H\d+$/;
const VOUCHERED_STATUS = 11;

const CreateHotel = () => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [canUpload, setCanUpload] = useState(false);
  const [form] = Form.useForm();
  const fetchTimerRef = useRef(null);
  const lastFetchedRef = useRef("");

  const clearAutoFields = () => {
    form.setFieldsValue({
      hotelName: "",
      guestName: "",
      contactNumber: "",
    });
  };

  const fetchHotelDetailsByRef = (rawRef, { silent = false } = {}) => {
    const refNo = String(rawRef ?? "").trim().toUpperCase();
    if (!refNo) {
      lastFetchedRef.current = "";
      setCanUpload(false);
      clearAutoFields();
      return;
    }
    if (!SYT_HOTEL_REF.test(refNo)) {
      lastFetchedRef.current = "";
      setCanUpload(false);
      clearAutoFields();
      return;
    }
    if (lastFetchedRef.current === refNo || fetching) {
      return;
    }
    lastFetchedRef.current = refNo;
    setFetching(true);
    ApiClient.get(`admin/hotel/createTicket/${encodeURIComponent(refNo)}`)
      .then((resp) => {
        const ok = resp?.statusCode === 200 || resp?.status === 200;
        if (!ok || !resp?.data) {
          lastFetchedRef.current = "";
          setCanUpload(false);
          clearAutoFields();
          message.error(resp?.message ?? "Booking not found");
          return;
        }
        const status = Number(resp.data.bookingStatus ?? 0);
        if (status !== VOUCHERED_STATUS) {
          setCanUpload(false);
          form.setFieldsValue({
            bookingId: resp.data.refNumber ?? refNo,
            hotelName: resp.data.hotelName ?? "",
            guestName: resp.data.guestName ?? "",
            contactNumber: resp.data.contactNumber ?? "",
          });
          if (status === 3) {
            message.error(
              "This ticket is CONFIRMED. Confirmation Number can only be uploaded on VOUCHERED tickets.",
            );
          } else if (status === 6) {
            message.error("This ticket is CANCELLED. Upload is not allowed.");
          } else {
            message.error(
              "Confirmation Number can only be uploaded on VOUCHERED tickets.",
            );
          }
          return;
        }
        setCanUpload(true);
        form.setFieldsValue({
          bookingId: resp.data.refNumber ?? refNo,
          hotelName: resp.data.hotelName ?? "",
          guestName: resp.data.guestName ?? "",
          contactNumber: resp.data.contactNumber ?? "",
        });
        if (!silent) {
          message.success("Ticket details loaded");
        }
      })
      .catch((err) => {
        lastFetchedRef.current = "";
        setCanUpload(false);
        clearAutoFields();
        message.error(err?.message ?? "Failed to load booking details");
      })
      .finally(() => setFetching(false));
  };

  const handleRefChange = (e) => {
    const refNo = String(e?.target?.value ?? "").trim().toUpperCase();
    if (fetchTimerRef.current) {
      clearTimeout(fetchTimerRef.current);
    }
    if (!refNo) {
      lastFetchedRef.current = "";
      setCanUpload(false);
      clearAutoFields();
      return;
    }
    if (!SYT_HOTEL_REF.test(refNo)) {
      lastFetchedRef.current = "";
      setCanUpload(false);
      clearAutoFields();
      return;
    }
    fetchTimerRef.current = setTimeout(() => {
      fetchHotelDetailsByRef(refNo, { silent: true });
    }, 350);
  };

  const onFormSubmit = (values) => {
    if (!canUpload) {
      message.error(
        "Confirmation Number can only be uploaded on VOUCHERED tickets.",
      );
      return;
    }
    const { bookingId, confirmationNumber } = values;
    if (!SYT_HOTEL_REF.test(String(bookingId ?? "").trim())) {
      message.error("Invalid Booking Reference Number");
      return;
    }
    if (!String(confirmationNumber ?? "").trim()) {
      message.error("Confirmation Number is required");
      return;
    }
    setLoading(true);
    ApiClient.post("admin/update/confirmationNumber", {
      bookingId: String(bookingId).trim(),
      confirmationNumber: String(confirmationNumber).trim(),
    })
      .then((res) => {
        if (res?.status === 200 || res?.message === "SUCCESS") {
          message.success("Confirmation Number uploaded successfully");
          lastFetchedRef.current = "";
          setCanUpload(false);
          form.resetFields();
        } else {
          message.error(res?.message ?? "Failed to update Confirmation Number");
        }
      })
      .catch((err) => {
        message.error(err?.message ?? "Failed to update Confirmation Number");
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="manage-markup-section">
      <Content className="container-fluid">
        <Row>
          <Col span={24}>
            <Form
              layout="vertical"
              form={form}
              onFinish={onFormSubmit}
              onFinishFailed={() => message.error("Please check all fields")}
            >
              <div className="card-gap">
                <div className="flight-depart-details">
                  <h4>Ticket Details</h4>
                  <Card title="Ticket Details" bordered={false}>
                    <Row gutter={16}>
                      <Col className="gutter-class" md={6} xs={24}>
                        <Form.Item
                          label="SYT-Refrence No"
                          name="bookingId"
                          rules={[{ required: true, message: "Required" }]}
                        >
                          <Input
                            placeholder="SYT-H56178800"
                            disabled={fetching}
                            onChange={handleRefChange}
                            onBlur={(e) =>
                              fetchHotelDetailsByRef(e?.target?.value)
                            }
                            onPressEnter={(e) =>
                              fetchHotelDetailsByRef(e?.target?.value)
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-class" md={6} xs={24}>
                        <Form.Item label="Hotel Name" name="hotelName">
                          <Input placeholder="Auto-filled from ticket" readOnly />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-class" md={6} xs={24}>
                        <Form.Item label="Guest Name" name="guestName">
                          <Input placeholder="Auto-filled from ticket" readOnly />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-class" md={6} xs={24}>
                        <Form.Item label="Contact Number" name="contactNumber">
                          <Input placeholder="Auto-filled from ticket" readOnly />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-class" md={6} xs={24}>
                        <Form.Item
                          label="Confirmation Number"
                          name="confirmationNumber"
                          rules={[{ required: true, message: "Required" }]}
                        >
                          <Input placeholder="Enter confirmation number manually" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                  <Card>
                    <Row gutter={16}>
                      <Col className="gutter-class" md={24} xs={24}>
                        <Form.Item className="bookoffline-submit">
                          <Button
                            loading={loading || fetching}
                            htmlType="submit"
                            disabled={!canUpload}
                          >
                            Submit
                          </Button>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                </div>
              </div>
            </Form>
          </Col>
        </Row>
      </Content>
    </div>
  );
};

export default CreateHotel;
